import { Checkbox } from '@mm/components';
import { useChatbotCRUDActions } from '@mm/state/actions/ChatbotCRUDActions';
import {
  ChatWidgetChannelDefinitions,
  ChatWidgetChannels,
} from '@mm/state/model/ChatbotModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { StoreState } from '@mm/state/store-interfaces';
import React from 'react';
import { useSelector } from 'react-redux';
import RadioInput from '../../../../../../common/components/RadioInput';
import SettingSection from '../../../components/SettingSection/SettingSection';
import './CustomerChatWidgetSettingsSection.pcss';

type Props = {
  usedInSlideout?: boolean;
  className?: string;
};

export const CustomerChatWidgetSettingsSection = ({
  usedInSlideout,
  className,
}: Props) => {
  const chatbot = useSelector(
    (state: StoreState) => state.chatbot.activeChatbot,
  );
  const chatbotCRUDActions = useChatbotCRUDActions();
  const modalActions = useModalActions();

  if (!chatbot) {
    return null;
  }
  const { chatWidgetChannel, webchatMarkDoneAfterInitialSend } = chatbot;
  const channelLabel = (v: ChatWidgetChannels) => {
    switch (v) {
      case 'omnichat':
        return 'Facebook Messenger & Webchat';
      case 'facebook':
        return 'Facebook Messenger';
      case 'web_chat':
        return 'Webchat';
      default:
        return 'Facebook Messenger & Webchat';
    }
  };

  const onInputChange = (k: ChatWidgetChannels) => {
    modalActions.openGeneralConfirmModal({
      modalClassname: 'customer-chat-widget-settings-section-confirm-modal',
      headerText: '',
      messageText: (
        <div>
          By changing support from{' '}
          <strong>{channelLabel(chatWidgetChannel)}</strong> to{' '}
          <strong>{channelLabel(k)}</strong> will affect ALL website chat
          widgets
        </div>
      ),
      onCloseHandler: ok => {
        if (ok) {
          updateChatWidgetChannel(k);
        }
      },
      okButtonText: 'Change',
      okButtonStyling: { blue: true },
      cancelButtonText: 'Cancel',
    });
  };
  const updateChatWidgetChannel = (k: ChatWidgetChannels) =>
    chatbotCRUDActions.update({ id: chatbot.id, chatWidgetChannel: k });

  const updateWebchatMarkDoneAfterInitialSend = (v: boolean) => {
    chatbotCRUDActions.update({
      id: chatbot.id,
      webchatMarkDoneAfterInitialSend: v,
    });
  };

  return (
    <div className="customer-chat-widget-settings-section">
      <SettingSection
        className={className}
        title={usedInSlideout ? '' : 'Chat Widget Settings'}
      >
        {Object.entries(ChatWidgetChannelDefinitions).map(([k, v]) => (
          <div
            key={k}
            className="customer-chat-widget-settings-section__chat-widget-setting"
          >
            <RadioInput
              name="chatWidgetSettings"
              checked={chatWidgetChannel === k}
              value={k}
              label={v}
              onChange={() => onInputChange(k as ChatWidgetChannels)}
            />
            {k === 'omnichat' && (
              <a
                className="customer-chat-widget-settings-section__chat-widget-setting-help-icon"
                href="https://customers.ai/help/article/lceo0wtp0l-omni-chat"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="far fa-question-circle" />
              </a>
            )}
          </div>
        ))}
      </SettingSection>
      {!usedInSlideout && (
        <SettingSection title="Webchat Conversation">
          <Checkbox
            labelHelpTextClassName="customer-chat-widget-settings-section__checkbox"
            isChecked={!!webchatMarkDoneAfterInitialSend}
            onToggle={() => {
              updateWebchatMarkDoneAfterInitialSend(
                !webchatMarkDoneAfterInitialSend,
              );
            }}
            label="Mark conversations as done after sending initial dialogue"
          />
        </SettingSection>
      )}
    </div>
  );
};
